/**
 * Copyright (c) 2016-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql, type TypedDocumentNode } from "@apollo/client";
import { useSuspenseQuery } from "@apollo/client/react";
import { Heading } from "@ndla/primitives";
import { styled } from "@ndla/styled-system/jsx";
import { type ReactNode, Suspense, useId } from "react";
import { useTranslation } from "react-i18next";
import { PageRainbowSpinner } from "../../components/PageSpinner";
import { TransportationNode } from "../../components/TransportationPage/TransportationPageNode";
import { TransportationPageNodeListGrid } from "../../components/TransportationPage/TransportationPageNodeListGrid";
import type { GQLLaunchpadQuery, GQLLaunchpadQueryVariables } from "../../graphqlTypes";
import { partitionResources } from "./getResourceGroups";
import { ResourceItem } from "./ResourceItem";

interface Props {
  parentId: string;
  rootId?: string;
}

const StyledNav = styled("nav", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "xsmall",
  },
});

const LayoutContainer = styled("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "xxlarge",
  },
});

export const Resources = ({ parentId, rootId }: Props) => (
  <Suspense fallback={<PageRainbowSpinner />}>
    <ResourcesContent parentId={parentId} rootId={rootId} />
  </Suspense>
);

const ResourcesContent = ({ parentId, rootId }: Props) => {
  const { t } = useTranslation();

  const { error, data } = useSuspenseQuery(resourcesQuery, {
    variables: {
      parentId: parentId,
      rootId: rootId,
    },
  });

  const node = data?.node;

  const { coreArticles, supplementaryArticles, learningpaths } = partitionResources(node?.children ?? []);

  if (error || !node?.children?.length) {
    return null;
  }

  return (
    <LayoutContainer>
      {!!coreArticles.length && (
        <NavSection title={t("launchpad.coreContentTitle")} variant="listItems">
          {coreArticles.map((resource) => (
            <ResourceItem key={resource.id} resource={resource} />
          ))}
        </NavSection>
      )}
      {!!learningpaths.length && (
        <NavSection title={t("launchpad.learningpathsTitle")} variant="listItems">
          {learningpaths.map((resource) => (
            <ResourceItem key={resource.id} resource={resource} />
          ))}
        </NavSection>
      )}
      {!!supplementaryArticles.length && (
        <NavSection title={t("launchpad.supplementaryContentTitle")} variant="listItems">
          {supplementaryArticles.map((resource) => (
            <ResourceItem key={resource.id} resource={resource} />
          ))}
        </NavSection>
      )}
    </LayoutContainer>
  );
};

interface NavSectionProps {
  title: string;
  children: ReactNode;
  variant: "listItems" | "cards";
}

const StyledOl = styled("ol", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "xxsmall",
  },
});

const NavSection = ({ title, children, variant }: NavSectionProps) => {
  const headingId = useId();
  return (
    <StyledNav aria-describedby={headingId}>
      <Heading id={headingId} textStyle="title.large" asChild consumeCss>
        <h2>{title}</h2>
      </Heading>
      {variant === "cards" ? (
        <TransportationPageNodeListGrid context="case">{children}</TransportationPageNodeListGrid>
      ) : (
        <StyledOl>{children}</StyledOl>
      )}
    </StyledNav>
  );
};

const resourcesQuery: TypedDocumentNode<GQLLaunchpadQuery, GQLLaunchpadQueryVariables> = gql`
  query launchpad($parentId: String!, $rootId: String) {
    node(id: $parentId, rootId: $rootId) {
      id
      name
      url
      children(nodeType: "RESOURCE") {
        id
        ...ResourceItem_Node
      }
      links {
        ...TransportationNode_Node
      }
      metadata {
        customFields
      }
    }
  }
  ${ResourceItem.fragments.node}
  ${TransportationNode.fragments.node}
`;
